/**
 * Selector of elements that can receive keyboard focus.
 */
export const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Keeps keyboard focus inside a container while it is open (modals, menus). Returns the
 * cleanup that restores focus to where it was.
 *
 * @param {HTMLElement} container -- The element to trap focus in.
 * @param {HTMLElement | null} initial -- What to focus first; the first focusable when omitted.
 * @returns A function that removes the trap and restores the previous focus.
 */
export const trapFocus = (container: HTMLElement, initial?: HTMLElement | null): (() => void) => {
  const previous = document.activeElement as HTMLElement | null;
  const focusables = (): HTMLElement[] =>
    Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE));
  (initial ?? focusables()[0] ?? container).focus();

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== 'Tab') {
      return;
    }
    const items = focusables();
    if (items.length === 0) {
      event.preventDefault();
      return;
    }
    const start = items[0] as HTMLElement;
    const end = items[items.length - 1] as HTMLElement;
    if (event.shiftKey && document.activeElement === start) {
      event.preventDefault();
      end.focus();
    } else if (!event.shiftKey && document.activeElement === end) {
      event.preventDefault();
      start.focus();
    }
  };
  container.addEventListener('keydown', onKeyDown);
  return () => {
    container.removeEventListener('keydown', onKeyDown);
    previous?.focus();
  };
};
